/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ManagedDeviceAgentState,
  ManagedDeviceAgentState$inboundSchema,
} from "./manageddeviceagentstate.js";

/**
 * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
 */
export const ManagedDeviceCoverageBucket = {
  AgentActive: "agent_active",
  AgentStale: "agent_stale",
  AgentOtherDevice: "agent_other_device",
  NoAgent: "no_agent",
  NoEmail: "no_email",
  UnresolvedEmail: "unresolved_email",
  Missing: "missing",
} as const;
/**
 * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
 */
export type ManagedDeviceCoverageBucket = OpenEnum<
  typeof ManagedDeviceCoverageBucket
>;

/**
 * Whether the agent that answers for an agent_active device verifiably applies its policy; agent_state.attestation says whether that agent runs on this machine. attested: its latest report applies the policy last served to the reporting user. outdated: it reports a different policy, or none. unreported: no report within the window, from an agent that predates reporting or has stopped sending. halted: the reporting agent's kill switch stopped enforcement on a blocked build. Omitted for every other bucket.
 */
export const ManagedDevicePolicyState = {
  Attested: "attested",
  Outdated: "outdated",
  Unreported: "unreported",
  Halted: "halted",
} as const;
/**
 * Whether the agent that answers for an agent_active device verifiably applies its policy; agent_state.attestation says whether that agent runs on this machine. attested: its latest report applies the policy last served to the reporting user. outdated: it reports a different policy, or none. unreported: no report within the window, from an agent that predates reporting or has stopped sending. halted: the reporting agent's kill switch stopped enforcement on a blocked build. Omitted for every other bucket.
 */
export type ManagedDevicePolicyState = OpenEnum<
  typeof ManagedDevicePolicyState
>;

/**
 * One device from a connected MDM's inventory, annotated with its agent-coverage bucket. What that bucket attests depends on the organization's matching mode: under device-level matching a device may be classified by its own hardware serial (this machine ran the agent) or, when no serial heartbeat exists for it, by its assigned-user email (only that user ran the agent somewhere). Under user-level matching only the latter applies.
 */
export type ManagedDevice = {
  /**
   * The latest sign of life that classified this device, a policy poll or a state report: the machine's own under device-level matching, otherwise its assigned user's. Omitted when no agent has ever synced.
   */
  agentLastSeenAt?: Date | undefined;
  /**
   * What a device's agent last reported it enforces. Carries no error text or user-facing message.
   */
  agentState?: ManagedDeviceAgentState | undefined;
  /**
   * Coverage classification for the device. What agent_active attests depends on the org's matching mode and on whether this device matched by serial or by assigned-user email.
   */
  coverageBucket: ManagedDeviceCoverageBucket;
  /**
   * The MDM's identifier for the device.
   */
  externalId: string;
  firstSeenAt: Date;
  /**
   * Device hostname.
   */
  hostname?: string | undefined;
  /**
   * Device row ID.
   */
  id: string;
  lastSeenAt: Date;
  /**
   * Last device check-in as reported by the MDM.
   */
  mdmLastCheckInAt?: Date | undefined;
  /**
   * When the device went absent from the MDM inventory. Omitted while present.
   */
  missingSince?: Date | undefined;
  /**
   * Operating system name.
   */
  osName?: string | undefined;
  /**
   * Operating system version.
   */
  osVersion?: string | undefined;
  /**
   * Whether the agent that answers for an agent_active device verifiably applies its policy; agent_state.attestation says whether that agent runs on this machine. attested: its latest report applies the policy last served to the reporting user. outdated: it reports a different policy, or none. unreported: no report within the window, from an agent that predates reporting or has stopped sending. halted: the reporting agent's kill switch stopped enforcement on a blocked build. Omitted for every other bucket.
   */
  policyState?: ManagedDevicePolicyState | undefined;
  /**
   * Provider the device was synced from.
   */
  provider: string;
  /**
   * Hardware serial number as reported by the MDM.
   */
  serialNumber?: string | undefined;
  /**
   * Assigned user's email exactly as the MDM reported it. Omitted when the MDM has no assignment.
   */
  userEmail?: string | undefined;
  /**
   * Resolved Speakeasy user for the assigned email. Omitted when the email is missing or does not resolve to an org member.
   */
  userId?: string | undefined;
};

/** @internal */
export const ManagedDeviceCoverageBucket$inboundSchema: z.ZodMiniType<
  ManagedDeviceCoverageBucket,
  unknown
> = openEnums.inboundSchema(ManagedDeviceCoverageBucket);

/** @internal */
export const ManagedDevicePolicyState$inboundSchema: z.ZodMiniType<
  ManagedDevicePolicyState,
  unknown
> = openEnums.inboundSchema(ManagedDevicePolicyState);

/** @internal */
export const ManagedDevice$inboundSchema: z.ZodMiniType<
  ManagedDevice,
  unknown
> = z.pipe(
  z.object({
    agent_last_seen_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    agent_state: z.optional(ManagedDeviceAgentState$inboundSchema),
    coverage_bucket: ManagedDeviceCoverageBucket$inboundSchema,
    external_id: z.string(),
    first_seen_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    hostname: z.optional(z.string()),
    id: z.string(),
    last_seen_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    mdm_last_check_in_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    missing_since: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    os_name: z.optional(z.string()),
    os_version: z.optional(z.string()),
    policy_state: z.optional(ManagedDevicePolicyState$inboundSchema),
    provider: z.string(),
    serial_number: z.optional(z.string()),
    user_email: z.optional(z.string()),
    user_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_last_seen_at": "agentLastSeenAt",
      "agent_state": "agentState",
      "coverage_bucket": "coverageBucket",
      "external_id": "externalId",
      "first_seen_at": "firstSeenAt",
      "last_seen_at": "lastSeenAt",
      "mdm_last_check_in_at": "mdmLastCheckInAt",
      "missing_since": "missingSince",
      "os_name": "osName",
      "os_version": "osVersion",
      "policy_state": "policyState",
      "serial_number": "serialNumber",
      "user_email": "userEmail",
      "user_id": "userId",
    });
  }),
);

export function managedDeviceFromJSON(
  jsonString: string,
): SafeParseResult<ManagedDevice, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ManagedDevice$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ManagedDevice' from JSON`,
  );
}
